import React, { useEffect } from 'react'
import {
  TripActivity as RecentActivityHotel,
  RecentActivityRentalCar
} from '@pcln/marketing'
import { AbandonedFlight } from '@pcln/trip-activity-components'
import useBootstrapData from '@/hooks/useBootstrapData'
import useSeti from '@/hooks/useSeti'
import { handleToggleFavorite } from './utils'
import {
  fireAbandonedSelectionClickEvent,
  fireAbandonedSelectionLoadEvent,
  fireAddToWishlistToggleEvent
} from './ga4'
import { type AbandonedSelection } from './types'

export default function AbandonedSelection({
  abandonedSelection,
  pageName,
  useSmallerView = false,
  isTripGroupedSelection = false,
  withSibling = false
}: {
  abandonedSelection: AbandonedSelection
  pageName: 'homepage' | 'landing'
  useSmallerView?: boolean
  isTripGroupedSelection?: boolean
  withSibling?: boolean
}) {
  const {
    appName,
    appVersion,
    signInStatus: { signedIn },
    webstats: { clientGUID: cguid }
  } = useBootstrapData()
  const hotelFavSeti =
    useSeti('HP_PUWYLO_FAVORITE', signedIn) === 'VARIANT' && signedIn

  const isSelectionTaxExcluded =
    useSeti(
      'HP_SELECTION_TAXEXC_PRICE',
      abandonedSelection.productType === 'hotels' &&
        abandonedSelection.priceDisplayRegulation ===
          'TAX_EXCL_TOTAL_PRICE_PROMINENT' &&
        !isTripGroupedSelection
    ) === 'VARIANT' &&
    abandonedSelection.productType === 'hotels' &&
    abandonedSelection.priceDisplayRegulation ===
      'TAX_EXCL_TOTAL_PRICE_PROMINENT' &&
    !isTripGroupedSelection

  useEffect(() => {
    if (abandonedSelection) {
      fireAbandonedSelectionLoadEvent(
        abandonedSelection,
        pageName,
        isTripGroupedSelection
      )
    }
  }, [abandonedSelection, pageName, isTripGroupedSelection])

  if (abandonedSelection.productType === 'cars') {
    return (
      <RecentActivityRentalCar
        {...abandonedSelection}
        subheadline={
          <>
            Pick up Location: {abandonedSelection.pickupLocation}
            {abandonedSelection.isDropOffAtDifferentLocation && (
              <>
                <br />
                Drop Off Location: {abandonedSelection.returnLocation}
              </>
            )}
          </>
        }
        onClick={() => {
          fireAbandonedSelectionClickEvent(
            abandonedSelection,
            pageName,
            isTripGroupedSelection
          )
        }}
        isSmallerView={useSmallerView}
        withSibling={withSibling}
        altText="Trip activity image"
        ariaLabel="trip activity button"
      />
    )
  }
  if (abandonedSelection.productType === 'flights') {
    const { headline, linkUrl } = abandonedSelection
    return (
      <AbandonedFlight
        {...abandonedSelection}
        heading={headline}
        url={linkUrl}
        buttonText="Continue Search"
        onClick={() => {
          fireAbandonedSelectionClickEvent(
            abandonedSelection,
            pageName,
            isTripGroupedSelection
          )
          window.open(linkUrl)
        }}
        isSmallerView={useSmallerView}
        withSibling={withSibling}
      />
    )
  }

  const hotelTotalPrice = isSelectionTaxExcluded
    ? abandonedSelection.hotelTotalPriceExcludingTaxes
    : abandonedSelection.hotelTotalPriceWithTaxesAndFeesAtAbandonment
  return hotelFavSeti ? (
    <RecentActivityHotel
      {...abandonedSelection}
      onClick={() => {
        fireAbandonedSelectionClickEvent(
          abandonedSelection,
          pageName,
          isTripGroupedSelection
        )
      }}
      isSmallerView={useSmallerView}
      withSibling={withSibling}
      altText="Trip activity image"
      ariaLabel="trip activity button"
      hotelPriceDetails={
        !isTripGroupedSelection && hotelTotalPrice?.amount
          ? {
              price: hotelTotalPrice.amount,
              currencySymbol: hotelTotalPrice.currencyPrefix
            }
          : undefined
      }
      onFavoriteToggle={async (isNowFavorite: boolean) => {
        fireAddToWishlistToggleEvent(isNowFavorite, pageName)
        if (abandonedSelection.id && abandonedSelection.cityId) {
          await handleToggleFavorite(
            {
              hotelId: abandonedSelection.id,
              cityId: abandonedSelection.cityId,
              cguid,
              appName,
              appVersion
            },
            isNowFavorite
          )
        }
      }}
      initialFavoriteState={abandonedSelection.isFavorite}
    />
  ) : (
    <RecentActivityHotel
      {...abandonedSelection}
      onClick={() => {
        fireAbandonedSelectionClickEvent(
          abandonedSelection,
          pageName,
          isTripGroupedSelection
        )
      }}
      isSmallerView={useSmallerView}
      withSibling={withSibling}
      altText="Trip activity image"
      ariaLabel="trip activity button"
      hotelPriceDetails={
        !isTripGroupedSelection && hotelTotalPrice?.amount
          ? {
              price: hotelTotalPrice.amount,
              currencySymbol: hotelTotalPrice.currencyPrefix
            }
          : undefined
      }
    />
  )
}
